import { authHeaders } from "@/lib/api";

const BASE = "/api/admin/inventory";
export type SerialMode = "REQUIRED" | "OPTIONAL" | "NONE" | "UNCONFIGURED";
export interface SerialPolicy {
  product_id: number;
  mode: SerialMode;
  policy_version: number;
}
export interface InventoryProduct {
  product_id: number;
  name: string;
  full_name?: string | null;
  ean?: string | null;
  model?: string | null;
  brand_raw?: string | null;
  stockable: boolean;
  serial_policy: SerialMode;
}
export interface InventoryLocation {
  location_id: number;
  name: string;
  kind: string;
}
export type BoxStatus =
  | "UNASSIGNED"
  | "ASSIGNED"
  | "VOIDED"
  | "QUARANTINE_PENDING_POLICY"
  | "QUARANTINE_PENDING_SERIALS"
  | "QUARANTINED";
export interface InventoryBox {
  box_id: string;
  status: BoxStatus;
  row_version: number;
  product_id: number | null;
  quantity: number | null;
  location_id: number | null;
  location_name: string | null;
  condition: string;
  serial_policy: SerialMode;
  serials: string[];
  assigned_by?: string | null;
  assigned_at?: string | null;
  created_at: string;
}
export interface LabelBatch {
  batch_id: string;
  quantity: number;
  codes?: string[];
  created_at?: string;
}
export interface StockRow {
  product_id: number;
  on_hand: number;
  quarantined: number;
  reserved: number;
  available: number;
  coverage: string;
}
export interface AuditEvent {
  event_id?: number | string;
  box_id?: string;
  event_type?: string;
  kind?: string;
  actor?: string;
  created_at?: string;
  reason?: string | null;
  quantity?: number;
  [key: string]: unknown;
}
export interface ReceiptInput {
  product_id: number;
  quantity: number;
  location_id: number;
  condition: string;
  serials: string[];
  expected_version: number;
}
export class InventoryApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
  ) {
    super(`HTTP ${status}: ${detail}`);
    this.name = "InventoryApiError";
  }
}
export function newInventoryKey(): string {
  return crypto.randomUUID();
}
async function jsonReq<T>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...init.headers,
      ...authHeaders(),
    },
  });
  if (!r.ok) {
    let detail = "";
    try {
      const body = await r.json();
      detail =
        typeof body.detail === "string"
          ? body.detail
          : JSON.stringify(body.detail ?? "");
    } catch {
      /* The HTTP status remains available for non-JSON errors. */
    }
    throw new InventoryApiError(r.status, detail);
  }
  return r.json() as Promise<T>;
}
function write<T>(
  path: string,
  body: unknown,
  key: string,
  method = "POST",
): Promise<T> {
  return jsonReq<T>(path, {
    method,
    body: JSON.stringify(body),
    headers: { "Idempotency-Key": key, "X-Inventory-Confirm": "true" },
  });
}
const boxPath = (id: string) => `/boxes/${encodeURIComponent(id)}`;
const policyPath = (id: number) => `/products/${id}/serial-policy`;
export function createLabelBatch(
  quantity: number,
  locationDefault?: number,
  key = newInventoryKey(),
): Promise<LabelBatch> {
  return write(
    "/labels/batches",
    {
      quantity,
      expected_version: 0,
      ...(locationDefault === undefined
        ? {}
        : { location_default: locationDefault }),
    },
    key,
  );
}
export function getLabelBatch(batchId: string): Promise<LabelBatch> {
  return jsonReq(`/labels/batches/${encodeURIComponent(batchId)}`);
}
export interface LabelBatchPage {
  batches: LabelBatch[];
  total: number;
  offset: number;
}
export function listLabelBatches(
  limit = 50,
  offset = 0,
): Promise<LabelBatchPage> {
  return jsonReq(
    `/labels/batches?${new URLSearchParams({ limit: String(limit), offset: String(offset) })}`,
  );
}
export function pdfUrl(
  batchId: string,
  layout?: {
    rows: number;
    columns: number;
    margin_pt: number;
    module_pt: number;
  },
): string {
  const params = layout
    ? `?${new URLSearchParams(Object.entries(layout).map(([k, v]) => [k, String(v)]))}`
    : "";
  return `${BASE}/labels/batches/${encodeURIComponent(batchId)}/pdf${params}`;
}
export async function fetchLabelPdf(
  batchId: string,
  layout?: Parameters<typeof pdfUrl>[1],
): Promise<Blob> {
  const response = await fetch(pdfUrl(batchId, layout), {
    credentials: "include",
    headers: { ...authHeaders(), Accept: "application/pdf" },
  });
  if (!response.ok) throw new InventoryApiError(response.status, "");
  return response.blob();
}
export function listBoxes(
  status?: string,
  limit = 50,
  offset = 0,
): Promise<{ boxes: InventoryBox[]; total: number }> {
  const p = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (status) p.set("status", status);
  return jsonReq(`/boxes?${p}`);
}
export function getBox(id: string): Promise<InventoryBox> {
  return jsonReq(boxPath(id));
}
export function assignBox(
  id: string,
  input: ReceiptInput,
  key: string,
): Promise<InventoryBox> {
  // Explicit allowlist: never forward client-supplied actor, role or customer fields.
  const {
    product_id,
    quantity,
    location_id,
    condition,
    serials,
    expected_version,
  } = input;
  return write(
    `${boxPath(id)}/assign`,
    { product_id, quantity, location_id, condition, serials, expected_version },
    key,
  );
}
export function completeBoxSerials(
  id: string,
  serials: string[],
  expected_version: number,
  key: string,
): Promise<InventoryBox> {
  return write(`${boxPath(id)}/serials`, { serials, expected_version }, key);
}
export function moveBox(
  id: string,
  location_id: number,
  reason: string,
  expected_version: number,
  key: string,
): Promise<InventoryBox> {
  return write(
    `${boxPath(id)}/move`,
    { location_id, reason, expected_version },
    key,
  );
}
export function quarantineBox(
  id: string,
  condition: string,
  reason: string,
  expected_version: number,
  key: string,
): Promise<InventoryBox> {
  return write(
    `${boxPath(id)}/quarantine`,
    { condition, reason, expected_version },
    key,
  );
}
export function voidBox(
  id: string,
  reason: string,
  expected_version: number,
  key: string,
): Promise<InventoryBox> {
  return write(`${boxPath(id)}/void`, { reason, expected_version }, key);
}
export function searchInventoryProducts(
  q: string,
): Promise<{ products: InventoryProduct[] }> {
  return jsonReq(`/products/search?${new URLSearchParams({ q })}`);
}
export function getSerialPolicy(id: number): Promise<SerialPolicy> {
  return jsonReq(policyPath(id));
}
export function setSerialPolicy(
  id: number,
  mode: Exclude<SerialMode, "UNCONFIGURED">,
  reason: string,
  expected_version: number,
  key: string,
): Promise<SerialPolicy> {
  return write(policyPath(id), { mode, reason, expected_version }, key, "PUT");
}
export function stockForProduct(id: number): Promise<StockRow> {
  return jsonReq(`/stock/${id}`);
}
export function listLocations(): Promise<{ locations: InventoryLocation[] }> {
  return jsonReq("/locations");
}
export function listAudit(
  box_id?: string,
  limit = 25,
  offset = 0,
): Promise<{ events: AuditEvent[]; total: number }> {
  const p = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (box_id) p.set("box_id", box_id);
  return jsonReq(`/audit?${p}`);
}
